import { appRoutes } from './routeConfig';
import { AppRoute } from '../../types/routeTypes';

export interface FlattenedRoute {
  path: string;
  key: string;
  label: string;
  access: string[];
  isPublic: boolean;
  allowedRoles: any[];
  children: any[];
  isActive: boolean;
  isDefault: boolean;
  order?: number;
}

export interface RouteComparison {
  missing: FlattenedRoute[];      // Routes in UI but not in DB
  outdated?: FlattenedRoute[];     // Routes that need updates
  extra?: any[];                    // Routes in DB but not in UI
  synced?: any[];                   // Routes that match
}

/**
 * Flatten nested routes into a single array
 */
export function flattenRoutes(routes: AppRoute[]): FlattenedRoute[] {
  const flattened: FlattenedRoute[] = [];

  routes.forEach(route => {
    const { children, component, ...parentRoute } = route as any;
    flattened.push({
      path: parentRoute.path,
      key: parentRoute.key,
      label: parentRoute.label,
      access: parentRoute.access || [],
      isPublic: parentRoute.isPublic || false,
      allowedRoles: parentRoute.allowedRoles || [],
      isActive: true,
      isDefault: true,
      order: parentRoute.order,
      children: children ? children.map((child: any) => ({
        key: child.key,
        label: child.label,
        path: child.path,
        access: child.access || [],
        allowedRoles: child.allowedRoles || [],
        // isPublic: child.isPublic || false,
        // isActive: true,
      })) : []
    });
  });

  return flattened;
}

const normalizeChildren = (children: any[]) =>
  children.map(c => ({
    key: c.key,
    label: c.label,
    path: c.path,
    access: [...(c.access || [])].sort(),
    isPublic: c.isPublic || false,
  }));

/**
 * Compare UI routes with database routes
 */
export function compareRoutes(
  uiRoutes: AppRoute[],
  dbRoutes: any[]
): RouteComparison {
  const flattenedUiRoutes = flattenRoutes(uiRoutes);

  // Create maps for faster lookup
  const dbRouteMap = new Map(dbRoutes.map(r => [r.path, r]));
  const uiRouteMap = new Map(flattenedUiRoutes.map(r => [r.path, r]));

  const missing = flattenedUiRoutes.filter(uiRoute => {
    const dbRoute = dbRouteMap.get(uiRoute.path);

    if (!dbRoute) return true;
    const uiChildren = normalizeChildren(uiRoute.children || []);
    const dbChildren = normalizeChildren(dbRoute.children || []);
    // Existing route but data changed
    return (
      dbRoute.label !== uiRoute.label || JSON.stringify(uiChildren) !== JSON.stringify(dbChildren)
    );
  });


  return { missing };
}